import React, { useState, useEffect } from "react";
import axios from "axios";

const TaskReports = () => {
  const [tasks, setTasks] = useState([]);
  const [totalTasks, setTotalTasks] = useState(0);
  const [completedTasks, setCompletedTasks] = useState(0);
  const [pendingTasks, setPendingTasks] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [selectedTaskNumber, setSelectedTaskNumber] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchTasks = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${apiUrl}/api/tasks`);
        const parsedTasks = Array.isArray(response.data) ? response.data : [];
        setTasks(parsedTasks);
        updateStats(parsedTasks);
      } catch (err) {
        console.warn(
          "Failed to fetch tasks from API, using localStorage:",
          err.message,
        );
        const savedTasks = localStorage.getItem("tasks");
        const parsedTasks = savedTasks ? JSON.parse(savedTasks) : [];
        setTasks(parsedTasks);
        updateStats(parsedTasks);
        if (parsedTasks.length === 0) {
          setError("No tasks available");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, []);

  const updateStats = (tasksList) => {
    const total = tasksList.length;
    const completed = tasksList.filter((task) => task.status === "done").length;
    const pending = total - completed;

    setTotalTasks(total);
    setCompletedTasks(completed);
    setPendingTasks(pending);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (apiUrl.startsWith("http"))
      return `${apiUrl}/public/storage/${imagePath}`;
    if (apiUrl.startsWith("https"))
      return `${apiUrl}/public/storage/${imagePath}`;
    return imagePath;
  };

  const completionRate =
    totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const openUploadModal = (taskId, taskNumber) => {
    setSelectedTaskId(taskId);
    setSelectedTaskNumber(taskNumber);
    setUploadFile(null);
    setUploadError(null);
    setUploadSuccess(null);
    setIsUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    setIsUploadModalOpen(false);
    setSelectedTaskId(null);
    setSelectedTaskNumber(null);
    setUploadFile(null);
    setUploadError(null);
    setUploadSuccess(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFile(file);
      setUploadError(null);
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();

    if (!uploadFile) {
      setUploadError("Please select a file to upload");
      return;
    }

    setUploadLoading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const formData = new FormData();
      formData.append("image", uploadFile);
      formData.append("task_id", selectedTaskId);

      const response = await axios.post(`${apiUrl}/api/task-img`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setUploadSuccess("File uploaded successfully!");

      setTimeout(() => {
        closeUploadModal();
        window.location.reload();
      }, 2000);
    } catch (err) {
      console.error("Upload error:", err);
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to upload file";
      setUploadError(errorMessage);
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-4 sm:py-6 md:py-8 max-w-7xl">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
            Task Reports
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Overview and analytics of all your tasks
          </p>
        </div>

        {/* Stats Cards - Optimized for mobile */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-white p-3 sm:p-4 md:p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">
              Total Tasks
            </h3>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600">
              {totalTasks}
            </p>
          </div>

          <div className="bg-white p-3 sm:p-4 md:p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">
              Completed
            </h3>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-green-600">
              {completedTasks}
            </p>
          </div>

          <div className="bg-white p-3 sm:p-4 md:p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">
              Pending
            </h3>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-orange-600">
              {pendingTasks}
            </p>
          </div>

          <div className="bg-white p-3 sm:p-4 md:p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-1">
              Completion Rate
            </h3>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-purple-600">
              {completionRate}%
            </p>
          </div>
        </div>

        {/* Tasks Section */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              All Tasks
            </h3>
            <span className="text-xs sm:text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
              {tasks.length} tasks
            </span>
          </div>

          {error && (
            <div className="mb-4 p-3 sm:p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-700">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="text-center py-8 sm:py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#23A19B]"></div>
              <p className="text-gray-500 mt-3 text-sm">Loading tasks...</p>
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <p className="text-gray-500 mt-2">No tasks found</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View - Primary for mobile */}
              <div className="space-y-3 sm:space-y-4">
                {tasks.map((task) => (
                  <div
                    key={task.id}
                    className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                  >
                    {/* Header with task number and status */}
                    <div className="flex justify-between items-center p-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Task #</p>
                        <p className="text-sm sm:text-base font-bold text-gray-900">
                          {task.task_number}
                        </p>
                      </div>
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                          task.status === "done"
                            ? "bg-green-100 text-green-800"
                            : "bg-orange-100 text-orange-800"
                        }`}
                      >
                        {task.status === "done" ? "✓ Completed" : "⏳ Pending"}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="p-4 space-y-3">
                      {/* Task Description */}
                      <div>
                        <p className="text-xs text-gray-500 font-medium mb-1">
                          Task Description
                        </p>
                        <p className="text-sm sm:text-base text-gray-900 leading-relaxed">
                          {task.task}
                        </p>
                      </div>

                      {/* Site */}
                      <div>
                        <p className="text-xs text-gray-500 font-medium mb-1">
                          Site Location
                        </p>
                        <p className="text-sm text-gray-700 flex items-center">
                          <svg
                            className="w-4 h-4 mr-1 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          {task.site}
                        </p>
                      </div>

                      {/* Date Range */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 font-medium mb-2">
                          Date Range
                        </p>
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <p className="text-xs text-gray-400">From</p>
                            <p className="text-sm font-medium text-gray-700">
                              {formatDate(task.date_from)}
                            </p>
                          </div>
                          <svg
                            className="w-4 h-4 text-gray-400 mx-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M14 5l7 7m0 0l-7 7m7-7H3"
                            />
                          </svg>
                          <div className="flex-1">
                            <p className="text-xs text-gray-400">To</p>
                            <p className="text-sm font-medium text-gray-700">
                              {formatDate(task.date_to)}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 pt-2">
                        {task.image ? (
                          <button
                            onClick={() =>
                              setSelectedImage(getImageUrl(task.image))
                            }
                            className="flex-1 inline-flex items-center justify-center px-3 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-all font-medium text-xs sm:text-sm cursor-pointer"
                          >
                            <svg
                              className="w-4 h-4 mr-1.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                            View Image
                          </button>
                        ) : (
                          <div className="flex-1 text-center px-3 py-2 rounded-lg bg-gray-100 text-gray-400 text-xs sm:text-sm">
                            No Image Available
                          </div>
                        )}
                        <button
                          onClick={() =>
                            openUploadModal(task.id, task.task_number)
                          }
                          className="flex-1 inline-flex items-center justify-center px-3 py-2 rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100 transition-all font-medium text-xs sm:text-sm cursor-pointer"
                        >
                          <svg
                            className="w-4 h-4 mr-1.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                            />
                          </svg>
                          Upload File
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View - Hidden on mobile, shown on large screens */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Task #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Task
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Site
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Image
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Upload
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        From Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        To Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {tasks.map((task) => (
                      <tr key={task.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {task.task_number}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-md">
                          <div className="truncate">{task.task}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {task.site}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {task.image ? (
                            <button
                              onClick={() =>
                                setSelectedImage(getImageUrl(task.image))
                              }
                              className="inline-flex items-center px-2.5 py-1.5 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors text-xs font-medium"
                            >
                              <svg
                                className="w-3.5 h-3.5 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                              View
                            </button>
                          ) : (
                            <span className="text-gray-400 text-xs">No image</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                              task.status === "done"
                                ? "bg-green-100 text-green-800"
                                : "bg-orange-100 text-orange-800"
                            }`}
                          >
                            {task.status === "done" ? "Completed" : "Pending"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() =>
                              openUploadModal(task.id, task.task_number)
                            }
                            className="inline-flex items-center px-2.5 py-1.5 rounded-md bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors text-xs font-medium"
                          >
                            <svg
                              className="w-3.5 h-3.5 mr-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                              />
                            </svg>
                            Upload
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(task.date_from)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(task.date_to)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Summary Section */}
        <div className="mt-4 sm:mt-6 bg-gradient-to-r from-[#23A19B] to-emerald-600 rounded-xl shadow-sm p-4 sm:p-6 text-white">
          <h3 className="text-sm sm:text-base font-semibold mb-3">Quick Summary</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <div className="text-xl sm:text-2xl font-bold">{totalTasks}</div>
              <div className="text-xs opacity-90">Total Tasks</div>
            </div>
            <div>
              <div className="text-xl sm:text-2xl font-bold">{completedTasks}</div>
              <div className="text-xs opacity-90">Completed</div>
            </div>
            <div>
              <div className="text-xl sm:text-2xl font-bold">{pendingTasks}</div>
              <div className="text-xs opacity-90">Pending</div>
            </div>
            <div>
              <div className="text-xl sm:text-2xl font-bold">{completionRate}%</div>
              <div className="text-xs opacity-90">Progress</div>
            </div>
          </div>
        </div>

        {/* Image Modal */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <div
              className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-base font-semibold text-gray-900">
                  Image Preview
                </h3>
                <button
                  onClick={() => setSelectedImage(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              <div className="p-4 flex justify-center items-center overflow-auto flex-1 bg-gray-900 rounded-b-xl">
                <img
                  src={selectedImage}
                  alt="Task preview"
                  className="max-w-full max-h-[calc(90vh-120px)] object-contain"
                />
              </div>
            </div>
          </div>
        )}

        {/* Upload Modal */}
        {isUploadModalOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
            onClick={closeUploadModal}
          >
            <div
              className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b px-4 sm:px-6 py-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                    Upload Task File
                  </h3>
                  <button
                    onClick={closeUploadModal}
                    className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                  >
                    ×
                  </button>
                </div>
              </div>

              <form onSubmit={handleUploadSubmit} className="p-4 sm:p-6 space-y-4">
                {/* Task Number Display */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Task Number
                  </label>
                  <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 text-sm font-semibold">
                    {selectedTaskNumber}
                  </div>
                </div>

                {/* Task ID Display */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Task ID
                  </label>
                  <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 text-sm">
                    {selectedTaskId}
                  </div>
                </div>

                {/* File Upload Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select File *
                  </label>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                    disabled={uploadLoading}
                    required
                  />
                  {uploadFile && (
                    <p className="text-xs text-green-600 mt-2 flex items-center">
                      <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {uploadFile.name} selected
                    </p>
                  )}
                </div>

                {/* Error Message */}
                {uploadError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{uploadError}</p>
                  </div>
                )}

                {/* Success Message */}
                {uploadSuccess && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700">{uploadSuccess}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeUploadModal}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
                    disabled={uploadLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    disabled={uploadLoading}
                  >
                    {uploadLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                          />
                        </svg>
                        Upload File
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskReports;